import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Seats } from '@ixicare/resources';

// The "Enable max concurrent alarm receivers" flag for the selected organization.
//
// The flag is not inherited: it applies to the organization it is set on and nowhere
// else, so enabling a whole customer tree means selecting each organization in turn.
// `mayEdit` is a property of the signed-in user rather than of the organization
// (Ixicare employees and root-organization admins), so one fetch covers the page.
export function useConcurrentBilling(organizationId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['concurrentBilling', organizationId],
    queryFn: () => Seats.getOrganizationConcurrentBilling(organizationId!),
    enabled: !!organizationId,
  });

  const mutation = useMutation({
    mutationFn: (enabled: boolean) => Seats.setOrganizationConcurrentBilling(organizationId!, enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(['concurrentBilling', organizationId], data);
      // Every row carries its own enabled flag, so the whole breakdown is stale.
      queryClient.invalidateQueries({ queryKey: ['billingBreakdown'] });
    },
  });

  return {
    enabled: query.data?.enabled ?? false,
    mayEdit: query.data?.mayEdit ?? false,
    isLoading: query.isLoading,
    setEnabled: mutation.mutate,
    isSaving: mutation.isPending,
  };
}
